/**
 * XYON Blog Library v2 — search-first landing-page interactivity
 *
 * The page has ONE filter surface — the search input. On focus a panel
 * opens below it; in the empty state it surfaces "popular questions" +
 * topic chips; once the user types, it shows categorised results across
 * Topics, Guides (articles), and Definitions (glossary terms).
 *
 * Search intelligence (this is the meaningful upgrade over a naïve
 * substring match):
 *
 *   1. Body content indexed.
 *      <script id="lib-search-index"> ships every article's title,
 *      summary, topic, stage, editor's-pick flag, and a 1200-char body
 *      excerpt as JSON. Search scores against all of these.
 *
 *   2. Body-anchored match snippets in the panel.
 *      When the query matches body content, we extract ~140 chars of
 *      context around the match and highlight it. Standard search-UX
 *      pattern (Algolia/Google). Big jump in scannability.
 *
 *   3. Topic context expansion.
 *      If the query *equals* a topic title (e.g. "Finasteride"), every
 *      article tagged with that topic gets a ranking boost — patient
 *      mental model is "finasteride" = "everything about finasteride."
 *
 *   4. Phrase boost.
 *      Multi-word queries get a separate score-pass for the exact
 *      phrase; an article saying "side effects" outranks one mentioning
 *      "side" and "effects" in different paragraphs.
 *
 *   5. Simple stemming.
 *      English suffixes (-s, -es, -ing, -ed, -ly, -ies, -ied) are
 *      stripped for matching, so "treating" hits articles about
 *      "treatment", "shedding" hits "shed", etc.
 *
 *   6. Glossary alias expansion.
 *      The xyon.glossary_term metaobject ships its aliases via the
 *      glossary-tooltip snippet. We build a synonym map so "thinning"
 *      maps to "androgenetic alopecia" and ranks accordingly.
 *
 * State (still tracked, no visible UI):
 *   state.topic — applied via panel chip click OR ?topic= URL param
 *   state.stage — only via ?stage= URL param (CRM/CTA deep links)
 *   state.sort  — only via ?sort= URL param; default 'recommended'
 *   state.q     — the search query (the primary control)
 *
 * Doesn't own: article-page TOC / scroll spy / glossary tooltips inside
 * the article body — those stay in blog-library.js.
 */

(function () {
  'use strict';

  var ROOT = document.querySelector('[data-lib-v2]');
  if (!ROOT) return;

  /* ─────────────────────────────────────────────
   * Elements
   * ───────────────────────────────────────────── */
  var searchArea     = ROOT.querySelector('[data-lib-search-area]');
  var searchInput    = ROOT.querySelector('[data-lib-search]');
  var searchClearBtn = ROOT.querySelector('[data-lib-search-clear]');
  var searchPanel    = ROOT.querySelector('[data-lib-search-panel]');
  var panelEmpty     = ROOT.querySelector('[data-lib-panel-empty]');
  var panelResults   = ROOT.querySelector('[data-lib-panel-results]');
  var panelNothing   = ROOT.querySelector('[data-lib-panel-nothing]');
  var resultsTopics  = ROOT.querySelector('[data-lib-results-topics]');
  var resultsArticles = ROOT.querySelector('[data-lib-results-articles]');
  var resultsGlossary = ROOT.querySelector('[data-lib-results-glossary]');
  var resultsTopicsList   = ROOT.querySelector('[data-lib-results-topics-list]');
  var resultsArticlesList = ROOT.querySelector('[data-lib-results-articles-list]');
  var resultsGlossaryList = ROOT.querySelector('[data-lib-results-glossary-list]');

  var featuredEl   = ROOT.querySelector('[data-lib-featured]');
  var grid         = ROOT.querySelector('[data-lib-grid]');
  var emptyEl      = ROOT.querySelector('[data-lib-empty]');
  var loadMoreBtn   = ROOT.querySelector('[data-lib-load-more]');
  var loadMoreLabel = ROOT.querySelector('[data-lib-load-more-label]');
  var countEl      = ROOT.querySelector('[data-lib-count]');
  var countPrefix  = ROOT.querySelector('[data-lib-count-prefix]');
  var clearBtns    = ROOT.querySelectorAll('[data-lib-clear]');
  var contextEl    = ROOT.querySelector('[data-lib-context]');
  var contextLabel = ROOT.querySelector('[data-lib-context-label]');

  var topicChips   = ROOT.querySelectorAll('[data-topic-handle]');
  var suggestions  = ROOT.querySelectorAll('[data-lib-suggest]');

  if (!grid || !searchInput) return;

  var INITIAL_COUNT = parseInt(grid.getAttribute('data-initial-count') || '12', 10);
  var visibleLimit = INITIAL_COUNT;

  /* ─────────────────────────────────────────────
   * Card index — used for grid display + fold logic. The DOM cards
   * remain the source of truth for rendering; the JSON search index
   * (below) is the source of truth for SCORING.
   * ───────────────────────────────────────────── */
  var cards = Array.from(grid.querySelectorAll('.lib-card'));
  var cardByUrl = {};
  cards.forEach(function (card) {
    var url = card.getAttribute('href');
    if (url) cardByUrl[url] = card;
  });

  /* ─────────────────────────────────────────────
   * Search index — JSON shipped inline by the section. Each entry:
   *   { u: url, t: title, s: summary, p: topicTitle, ph: topicHandle,
   *     g: stage, b: bodyExcerpt, e: editorsPick (0|1) }
   * ───────────────────────────────────────────── */
  var searchIndex = [];
  var indexEl = document.getElementById('lib-search-index');
  if (indexEl) {
    try {
      var parsed = JSON.parse(indexEl.textContent);
      if (Array.isArray(parsed)) searchIndex = parsed;
    } catch (e) {}
  }
  /* Fallback — if the JSON block didn't ship (older cached section,
     etc.), build a minimal index from card DOM so search still works. */
  if (searchIndex.length === 0) {
    cards.forEach(function (card) {
      var titleEl = card.querySelector('.lib-card__title');
      var sumEl   = card.querySelector('.lib-card__summary');
      var chipEl  = card.querySelector('.lib-card__topic-chip');
      searchIndex.push({
        u: card.getAttribute('href') || '',
        t: titleEl ? titleEl.textContent.trim() : '',
        s: sumEl   ? sumEl.textContent.trim()   : '',
        p: chipEl  ? chipEl.textContent.trim()  : '',
        ph: card.getAttribute('data-topic') || '',
        g: card.getAttribute('data-funnel') || '',
        b: '',
        e: card.getAttribute('data-editors-pick') === 'true' ? 1 : 0
      });
    });
  }

  /* Pre-lowercase the searchable fields once so we don't pay
     toLowerCase() cost on every keystroke. */
  searchIndex.forEach(function (entry) {
    entry._tL = (entry.t || '').toLowerCase();
    entry._sL = (entry.s || '').toLowerCase();
    entry._pL = (entry.p || '').toLowerCase();
    entry._bL = (entry.b || '').toLowerCase();
  });

  /* ─────────────────────────────────────────────
   * Glossary — for synonym expansion + the panel's Definitions row.
   * ───────────────────────────────────────────── */
  var glossary = [];
  var glossaryEl = document.getElementById('glossary-data');
  if (glossaryEl) {
    try {
      var rawGloss = JSON.parse(glossaryEl.textContent);
      if (Array.isArray(rawGloss)) glossary = rawGloss;
    } catch (e) {}
  }
  var synonymMap = {};
  glossary.forEach(function (g) {
    var canonical = (g.term || '').toLowerCase().trim();
    if (!canonical) return;
    synonymMap[canonical] = canonical;
    if (Array.isArray(g.aliases)) {
      g.aliases.forEach(function (alias) {
        var a = (alias || '').toLowerCase().trim();
        if (a) synonymMap[a] = canonical;
      });
    }
  });

  /* ─────────────────────────────────────────────
   * Stemming (Porter subset) — strip common English suffixes so casual
   * verb forms ↔ formal noun forms match.
   *   treating → treat   shedding → shed
   *   balding  → bald    losses   → loss
   * Conservative: skip short words, skip protected -ss endings.
   * ───────────────────────────────────────────── */
  function stem(word) {
    var w = (word || '').toLowerCase();
    if (w.length < 4) return w;
    if (/ies$/.test(w))         return w.slice(0, -3) + 'y';
    if (/ied$/.test(w))         return w.slice(0, -3) + 'y';
    if (/ing$/.test(w))         return w.slice(0, -3);
    if (/edly$/.test(w))        return w.slice(0, -4);
    if (/ed$/.test(w))          return w.slice(0, -2);
    if (/ly$/.test(w))          return w.slice(0, -2);
    if (/es$/.test(w))          return w.slice(0, -2);
    if (/ss$/.test(w))          return w; // preserve -ss
    if (/s$/.test(w))           return w.slice(0, -1);
    return w;
  }

  function tokenize(q) {
    return (q || '').toLowerCase().trim()
      .replace(/[^\w\s'-]/g, ' ')
      .split(/\s+/)
      .filter(function (t) { return t.length > 0; });
  }

  /* expandQuery — return the set of phrases equivalent to the query via
     the glossary alias map. Includes the original query plus any alias
     whose key starts with the query (so partial typing — "fina" — still
     finds "finasteride" via alias key match). */
  function expandQuery(q) {
    var lower = (q || '').toLowerCase().trim();
    if (!lower) return [lower];
    var out = { };
    out[lower] = true;
    if (synonymMap[lower]) out[synonymMap[lower]] = true;
    Object.keys(synonymMap).forEach(function (alias) {
      if (alias === lower) return;
      if (alias.indexOf(lower) === 0) {
        out[alias] = true;
        out[synonymMap[alias]] = true;
      }
    });
    return Object.keys(out);
  }

  /* ─────────────────────────────────────────────
   * Topic metadata — from rendered chips
   * ───────────────────────────────────────────── */
  var topicData = {};
  topicChips.forEach(function (chip) {
    var h = chip.getAttribute('data-topic-handle');
    var t = chip.getAttribute('data-topic-title') || h;
    if (!h) return;
    topicData[h] = { handle: h, title: t, titleLower: (t || '').toLowerCase() };
  });

  /* ─────────────────────────────────────────────
   * State + URL sync
   * ───────────────────────────────────────────── */
  var state = { topic: 'all', stage: 'all', sort: 'recommended', q: '' };
  var isHydrating = false;
  var panelOpen = false;

  var KNOWN_STAGES = ['problem_aware', 'solution_aware', 'consideration', 'conversion', 'retention', 'general_awareness'];
  var STAGE_LABELS = {
    problem_aware:     'Noticing changes',
    solution_aware:    'Exploring treatment',
    consideration:     'Reviewing evidence',
    conversion:        'Choosing a treatment',
    retention:         'On treatment',
    general_awareness: 'General awareness'
  };

  /* STAGE_PRIORITY drives the default grid sort + a search-scoring nudge.
     Highest priority (retention) bubbles to the top when no query is
     active; lowest (general_awareness) falls to the bottom. Articles
     with a stage value not in this map get priority 0. */
  var STAGE_PRIORITY = {
    retention:         6,
    conversion:        5,
    consideration:     4,
    solution_aware:    3,
    problem_aware:     2,
    general_awareness: 1
  };
  function validateStage(s) { return KNOWN_STAGES.indexOf(s) !== -1 ? s : 'all'; }
  function validateSort(s)  { return ['recommended', 'recent', 'shortest'].indexOf(s) !== -1 ? s : 'recommended'; }

  function syncURL(pushHistory) {
    if (isHydrating) return;
    try {
      var url = new URL(window.location.href);
      function set(key, value, def) {
        if (value === def || value === '' || value == null) url.searchParams.delete(key);
        else url.searchParams.set(key, value);
      }
      set('topic', state.topic, 'all');
      set('stage', state.stage, 'all');
      set('sort',  state.sort,  'recommended');
      set('q',     state.q,     '');
      url.searchParams.delete('funnel'); // legacy alias
      if (pushHistory) window.history.pushState({ lib2: true }, '', url);
      else window.history.replaceState({ lib2: true }, '', url);
    } catch (e) {}
  }

  function hydrateFromURL() {
    isHydrating = true;
    try {
      var p = new URLSearchParams(window.location.search);
      state.topic = topicData[p.get('topic')] ? p.get('topic') : 'all';
      state.stage = validateStage(p.get('stage') || p.get('funnel'));
      state.sort  = validateSort(p.get('sort'));
      state.q     = p.get('q') || '';
      if (searchInput) searchInput.value = state.q;
    } catch (e) {}
    isHydrating = false;
  }

  /* ─────────────────────────────────────────────
   * Search core — scoring with body content, phrase boost, stemming,
   * topic context expansion, and synonym expansion. Returns an ordered
   * array of index entries (highest score first).
   * ───────────────────────────────────────────── */
  function searchArticles(q, limit) {
    var qLower = (q || '').toLowerCase().trim();
    if (!qLower) return [];

    var tokens = tokenize(qLower);
    var stems = tokens.map(stem);
    var isPhrase = qLower.indexOf(' ') !== -1;

    /* Topic-context expansion: if the query equals a known topic title,
       boost all articles tagged with that topic. */
    var topicBoostHandle = null;
    Object.keys(topicData).forEach(function (h) {
      if (topicData[h].titleLower === qLower) topicBoostHandle = h;
    });

    /* Synonym phrases beyond the original query. */
    var synonymPhrases = expandQuery(qLower).filter(function (p) { return p !== qLower; });

    /* Unique stem+token set for per-term scoring. */
    var termSet = {};
    tokens.forEach(function (t) { if (t.length >= 2) termSet[t] = true; });
    stems.forEach(function (s) { if (s.length >= 2) termSet[s] = true; });
    var terms = Object.keys(termSet);

    var scored = [];
    for (var i = 0; i < searchIndex.length; i++) {
      var entry = searchIndex[i];
      var title = entry._tL;
      var summary = entry._sL;
      var topic = entry._pL;
      var body = entry._bL;
      var score = 0;

      /* 1. PHRASE BOOST — multi-word query, exact-phrase match. Heavy
         weight because phrase matches are far more specific than
         scattered token matches. */
      if (isPhrase) {
        if (title.indexOf(qLower) === 0)     score += 220;
        else if (title.indexOf(qLower) > 0)  score += 140;
        if (topic.indexOf(qLower) !== -1)    score += 80;
        if (summary.indexOf(qLower) !== -1)  score += 50;
        if (body.indexOf(qLower) !== -1)     score += 30;
      }

      /* 2. PER-TERM SCORING (tokens + stems) */
      for (var t = 0; t < terms.length; t++) {
        var term = terms[t];
        if (title.indexOf(term) === 0)     score += 100;
        else if (title.indexOf(term) > 0)  score += 55;
        if (topic.indexOf(term) !== -1)    score += 30;
        if (summary.indexOf(term) !== -1)  score += 15;
        if (body.indexOf(term) !== -1)     score += 5;
      }

      /* 3. SYNONYM EXPANSION (glossary aliases) — weighted lower than
         the literal query so synonym matches don't out-rank exact ones. */
      for (var p = 0; p < synonymPhrases.length; p++) {
        var phrase = synonymPhrases[p];
        if (title.indexOf(phrase) !== -1)   score += 40;
        if (topic.indexOf(phrase) !== -1)   score += 22;
        if (summary.indexOf(phrase) !== -1) score += 12;
        if (body.indexOf(phrase) !== -1)    score += 5;
      }

      /* 4. TOPIC CONTEXT BOOST */
      if (topicBoostHandle && entry.ph === topicBoostHandle) {
        score += 35;
      }

      /* 5. EDITOR'S-PICK BOOST */
      if (entry.e === 1) score += 5;

      /* 6. STAGE-PRIORITY NUDGE — small bonus so retention articles
         edge out problem_aware ones when relevance is otherwise close.
         Range: 1-6 → +2 to +12. Won't override strong title matches but
         breaks ties in the user's favour. */
      score += (STAGE_PRIORITY[entry.g] || 0) * 2;

      if (score > 0) scored.push({ entry: entry, score: score });
    }

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      /* tiebreak: editor's-pick first, then by url (stable) */
      var aE = a.entry.e || 0, bE = b.entry.e || 0;
      if (aE !== bE) return bE - aE;
      return (a.entry.u || '') < (b.entry.u || '') ? -1 : 1;
    });

    if (typeof limit === 'number') scored = scored.slice(0, limit);
    return scored.map(function (s) { return s.entry; });
  }

  function searchTopics(q, limit) {
    var qLower = (q || '').toLowerCase().trim();
    if (!qLower) return [];
    var matches = [];
    Object.keys(topicData).forEach(function (h) {
      var title = topicData[h].titleLower;
      if (title.indexOf(qLower) === -1) return;
      var count = 0;
      for (var i = 0; i < searchIndex.length; i++) {
        if (searchIndex[i].ph === h) count++;
      }
      matches.push({ handle: h, title: topicData[h].title, count: count, score: title.indexOf(qLower) === 0 ? 2 : 1 });
    });
    matches.sort(function (a, b) { return b.score - a.score; });
    if (typeof limit === 'number') matches = matches.slice(0, limit);
    return matches;
  }

  function searchGlossary(q, limit) {
    var qLower = (q || '').toLowerCase().trim();
    if (!qLower) return [];
    var matches = [];
    glossary.forEach(function (g) {
      var term = (g.term || '').toLowerCase();
      var def  = (g.definition || '').toLowerCase();
      var aliases = (g.aliases || []).map(function (a) { return (a || '').toLowerCase(); });
      var score = 0;
      if (term.indexOf(qLower) === 0)     score = 100;
      else if (term.indexOf(qLower) > 0)  score = 60;
      else if (aliases.some(function (a) { return a.indexOf(qLower) !== -1; })) score = 40;
      else if (def.indexOf(qLower) !== -1) score = 10;
      if (score > 0) matches.push({ entry: g, score: score });
    });
    matches.sort(function (a, b) { return b.score - a.score; });
    if (typeof limit === 'number') matches = matches.slice(0, limit);
    return matches.map(function (m) { return m.entry; });
  }

  /* ─────────────────────────────────────────────
   * Snippet extraction — given a text blob and query, find the
   * earliest match and extract ~140 chars of surrounding context.
   * Used for body-anchored result snippets.
   * ───────────────────────────────────────────── */
  function extractSnippet(text, q, maxLength) {
    maxLength = maxLength || 140;
    if (!text || !q) return '';
    var lower = text.toLowerCase();
    var qLower = q.toLowerCase().trim();

    /* Find earliest match — phrase first, then any token, then any stem. */
    var matchIdx = -1;
    var matchTerm = '';

    var phraseIdx = lower.indexOf(qLower);
    if (phraseIdx !== -1) {
      matchIdx = phraseIdx;
      matchTerm = qLower;
    } else {
      var tokens = tokenize(qLower);
      for (var i = 0; i < tokens.length; i++) {
        var idx = lower.indexOf(tokens[i]);
        if (idx !== -1 && (matchIdx === -1 || idx < matchIdx)) {
          matchIdx = idx;
          matchTerm = tokens[i];
        }
        var sIdx = lower.indexOf(stem(tokens[i]));
        if (sIdx !== -1 && (matchIdx === -1 || sIdx < matchIdx)) {
          matchIdx = sIdx;
          matchTerm = stem(tokens[i]);
        }
      }
    }

    if (matchIdx === -1) return '';

    /* Extract a window around the match, trimmed to word boundaries. */
    var contextBefore = 60;
    var contextAfter = maxLength - contextBefore - matchTerm.length;
    var start = Math.max(0, matchIdx - contextBefore);
    var end = Math.min(text.length, matchIdx + matchTerm.length + contextAfter);

    var snippet = text.substring(start, end);
    if (start > 0) {
      var firstSpace = snippet.indexOf(' ');
      if (firstSpace > 0 && firstSpace < 24) snippet = snippet.substring(firstSpace + 1);
      snippet = '…' + snippet;
    }
    if (end < text.length) {
      var lastSpace = snippet.lastIndexOf(' ');
      if (lastSpace > 0 && lastSpace > snippet.length - 24) snippet = snippet.substring(0, lastSpace);
      snippet = snippet + '…';
    }

    return snippet;
  }

  /* ─────────────────────────────────────────────
   * HTML helpers
   * ───────────────────────────────────────────── */
  function escapeHTML(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* Highlight ALL occurrences of any query token / phrase / stem in
     the given text. Used by both result titles and snippets. */
  function highlightMulti(text, q) {
    if (!q || !text) return escapeHTML(text);
    var t = String(text);
    var qLower = q.toLowerCase().trim();

    /* Build a set of phrases to highlight. */
    var phrases = { };
    phrases[qLower] = true;
    tokenize(qLower).forEach(function (tok) {
      if (tok.length >= 2) phrases[tok] = true;
      var s = stem(tok);
      if (s.length >= 2) phrases[s] = true;
    });

    /* Build a regex that matches any phrase (sorted longest-first so
       longer matches consume before shorter ones). */
    var phraseList = Object.keys(phrases).sort(function (a, b) { return b.length - a.length; });
    if (phraseList.length === 0) return escapeHTML(t);

    var escaped = phraseList.map(function (p) {
      return p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    });
    var re = new RegExp('(' + escaped.join('|') + ')', 'gi');

    /* Split text by the regex and wrap matches. Working on the raw
       string then escaping non-match segments separately keeps the
       output safe. */
    var parts = t.split(re);
    var out = '';
    for (var i = 0; i < parts.length; i++) {
      if (i % 2 === 1) {
        out += '<mark>' + escapeHTML(parts[i]) + '</mark>';
      } else {
        out += escapeHTML(parts[i]);
      }
    }
    return out;
  }

  /* ─────────────────────────────────────────────
   * Render panel results
   * ───────────────────────────────────────────── */
  function renderPanelResults(q) {
    var topicMatches    = searchTopics(q, 3);
    var articleMatches  = searchArticles(q, 6);
    var glossaryMatches = searchGlossary(q, 3);

    /* Topics */
    if (topicMatches.length > 0) {
      resultsTopicsList.innerHTML = topicMatches.map(function (t) {
        return '<li><button type="button" class="lib2-result" data-result-topic="' + escapeHTML(t.handle) + '" role="option">' +
          '<span class="lib2-result__title">' + highlightMulti(t.title, q) + '</span>' +
          '<span class="lib2-result__meta">' + t.count + ' ' + (t.count === 1 ? 'guide' : 'guides') + '</span>' +
          '</button></li>';
      }).join('');
      resultsTopics.hidden = false;
    } else {
      resultsTopics.hidden = true;
    }

    /* Guides — body-anchored snippet when the match is in the body */
    if (articleMatches.length > 0) {
      resultsArticlesList.innerHTML = articleMatches.map(function (entry) {
        var url = entry.u || '#';
        /* Try body first (richest), fall back to summary. Skip if the
           title already contains the match (snippet would be redundant). */
        var titleHasMatch = entry._tL.indexOf((q || '').toLowerCase().trim()) !== -1;
        var snippet = '';
        if (!titleHasMatch) {
          snippet = extractSnippet(entry.b, q, 140) || extractSnippet(entry.s, q, 140);
        }
        return '<li><a class="lib2-result" href="' + escapeHTML(url) + '" role="option">' +
          '<span class="lib2-result__body">' +
            '<span class="lib2-result__title">' + highlightMulti(entry.t || '', q) + '</span>' +
            (snippet ? '<span class="lib2-result__snippet">' + highlightMulti(snippet, q) + '</span>' : '') +
          '</span>' +
          '</a></li>';
      }).join('');
      resultsArticles.hidden = false;
    } else {
      resultsArticles.hidden = true;
    }

    /* Glossary */
    if (glossaryMatches.length > 0) {
      resultsGlossaryList.innerHTML = glossaryMatches.map(function (g) {
        var url = g.relatedUrl || '';
        var defSnippet = (g.definition || '').slice(0, 150);
        if ((g.definition || '').length > 150) defSnippet += '…';
        var inner = '<span class="lib2-result__icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
              '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>' +
            '</svg></span>' +
          '<span class="lib2-result__body">' +
            '<span class="lib2-result__title">' + highlightMulti(g.term || '', q) + '</span>' +
            '<span class="lib2-result__snippet">' + highlightMulti(defSnippet, q) + '</span>' +
          '</span>';
        if (url) {
          return '<li><a class="lib2-result" href="' + escapeHTML(url) + '" role="option">' + inner + '</a></li>';
        }
        return '<li><span class="lib2-result" role="option" style="cursor:default;">' + inner + '</span></li>';
      }).join('');
      resultsGlossary.hidden = false;
    } else {
      resultsGlossary.hidden = true;
    }

    var anyMatches = topicMatches.length + articleMatches.length + glossaryMatches.length > 0;
    if (panelNothing) panelNothing.hidden = anyMatches;
  }

  /* ─────────────────────────────────────────────
   * Panel show / hide
   * ───────────────────────────────────────────── */
  function showPanel() {
    panelOpen = true;
    searchPanel.hidden = false;
    searchInput.setAttribute('aria-expanded', 'true');
    if ((state.q || '').trim()) {
      panelEmpty.hidden = true;
      panelResults.hidden = false;
      renderPanelResults(state.q);
    } else {
      panelEmpty.hidden = false;
      panelResults.hidden = true;
    }
  }

  function hidePanel() {
    panelOpen = false;
    searchPanel.hidden = true;
    searchInput.setAttribute('aria-expanded', 'false');
  }

  /* ─────────────────────────────────────────────
   * Grid filter — uses the search index for query matching now, so
   * the grid reflects the same scored ranking as the panel.
   * ───────────────────────────────────────────── */
  function applyFilters() {
    var q = (state.q || '').trim();
    var topicActive = state.topic !== 'all';
    var stageActive = state.stage !== 'all';
    var queryActive = q !== '';
    var anyFilterActive = topicActive || stageActive || queryActive;

    /* Build the ordered list of URLs that should be visible. */
    var orderedUrls;
    if (queryActive) {
      var ranked = searchArticles(q); // unlimited
      orderedUrls = ranked
        .filter(function (e) {
          if (topicActive && e.ph !== state.topic) return false;
          if (stageActive && e.g !== state.stage) return false;
          return true;
        })
        .map(function (e) { return e.u; });
    } else {
      /* No query — sort all index entries by the active sort, then
         filter by topic+stage. Default sort (recommended) prioritises
         articles by funnel stage: retention > conversion > consideration
         > solution_aware > problem_aware > general_awareness. Editor's
         pick is a secondary tiebreak; published date is the final one. */
      var sorted = searchIndex.slice().sort(function (a, b) {
        if (state.sort === 'shortest') {
          /* Cards still hold read-time; use that for sort. */
          var ca = cardByUrl[a.u];
          var cb = cardByUrl[b.u];
          var ar = ca ? parseInt(ca.getAttribute('data-read-time') || '0', 10) : 0;
          var br = cb ? parseInt(cb.getAttribute('data-read-time') || '0', 10) : 0;
          return ar - br;
        }
        if (state.sort === 'recommended') {
          /* Primary: stage priority desc */
          var aPri = STAGE_PRIORITY[a.g] || 0;
          var bPri = STAGE_PRIORITY[b.g] || 0;
          if (aPri !== bPri) return bPri - aPri;
          /* Secondary: editor's pick */
          var ap = a.e || 0, bp = b.e || 0;
          if (ap !== bp) return bp - ap;
        }
        /* Final tiebreak: published_at desc (also the 'recent' sort) */
        var caRec = cardByUrl[a.u];
        var cbRec = cardByUrl[b.u];
        var ad = caRec ? caRec.getAttribute('data-published-at') || '' : '';
        var bd = cbRec ? cbRec.getAttribute('data-published-at') || '' : '';
        if (ad === bd) return 0;
        return ad < bd ? 1 : -1;
      });
      orderedUrls = sorted
        .filter(function (e) {
          if (topicActive && e.ph !== state.topic) return false;
          if (stageActive && e.g !== state.stage) return false;
          return true;
        })
        .map(function (e) { return e.u; });
    }

    /* Reorder cards in DOM to match orderedUrls. */
    orderedUrls.forEach(function (url) {
      var card = cardByUrl[url];
      if (card) grid.appendChild(card);
    });

    /* Visibility pass: ALL cards start hidden; reveal only the first
       `visibleLimit` cards from orderedUrls. Iterating orderedUrls is
       critical so the fold matches the SORTED order, not the original
       DOM-load order (a prior bug revealed wrong stages). */
    cards.forEach(function (card) { card.hidden = true; });
    for (var oi = 0; oi < orderedUrls.length && oi < visibleLimit; oi++) {
      var visCard = cardByUrl[orderedUrls[oi]];
      if (visCard) visCard.hidden = false;
    }

    /* Load more */
    if (loadMoreBtn) {
      var passedCount = orderedUrls.length;
      var remaining = passedCount - visibleLimit;
      if (remaining > 0) {
        loadMoreBtn.hidden = false;
        if (loadMoreLabel) {
          loadMoreLabel.textContent = 'Show ' + remaining + ' more ' + (remaining === 1 ? 'guide' : 'guides');
        }
      } else {
        loadMoreBtn.hidden = true;
      }
    }

    /* Count + "All" prefix. */
    var passed = orderedUrls.length;
    if (countEl) countEl.textContent = String(passed);
    if (countPrefix) countPrefix.hidden = anyFilterActive;

    if (emptyEl) emptyEl.hidden = passed > 0;
    if (featuredEl) featuredEl.hidden = anyFilterActive;

    /* Context pill — topic / stage only (search has its own input). */
    if (contextEl && contextLabel) {
      if (topicActive || stageActive) {
        var parts = [];
        if (topicActive && topicData[state.topic]) parts.push(topicData[state.topic].title);
        if (stageActive) parts.push(STAGE_LABELS[state.stage] || state.stage);
        contextLabel.textContent = parts.join(' · ');
        contextEl.hidden = false;
      } else {
        contextEl.hidden = true;
      }
    }
  }

  function refresh(opts) {
    opts = opts || {};
    if (opts.resetFold) visibleLimit = INITIAL_COUNT;
    applyFilters();
    syncURL(opts.pushHistory === true);
  }

  /* ─────────────────────────────────────────────
   * Rotating placeholder
   * ───────────────────────────────────────────── */
  var rotatingPlaceholders = [];
  var rpAttr = searchInput.getAttribute('data-rotating-placeholders') || '';
  if (rpAttr) {
    rotatingPlaceholders = rpAttr.split('||').map(function (s) { return s.trim(); }).filter(Boolean);
  }
  if (rotatingPlaceholders.length > 1) {
    var rpIdx = 0;
    setInterval(function () {
      if (document.activeElement === searchInput) return;
      if (searchInput.value) return;
      rpIdx = (rpIdx + 1) % rotatingPlaceholders.length;
      searchInput.setAttribute('data-rotating', 'true');
      setTimeout(function () {
        searchInput.placeholder = rotatingPlaceholders[rpIdx];
        searchInput.removeAttribute('data-rotating');
      }, 240);
    }, 3500);
  }

  /* ─────────────────────────────────────────────
   * Event bindings
   * ───────────────────────────────────────────── */
  var inputDebounce;
  searchInput.addEventListener('input', function () {
    state.q = searchInput.value;
    if (searchClearBtn) searchClearBtn.hidden = !state.q;
    if (panelOpen) {
      if ((state.q || '').trim()) {
        panelEmpty.hidden = true;
        panelResults.hidden = false;
        renderPanelResults(state.q);
      } else {
        panelEmpty.hidden = false;
        panelResults.hidden = true;
      }
    }
    clearTimeout(inputDebounce);
    inputDebounce = setTimeout(function () {
      refresh({ resetFold: true, pushHistory: false });
    }, 120);
  });

  searchInput.addEventListener('focus', function () { showPanel(); });

  searchInput.addEventListener('blur', function () {
    setTimeout(function () {
      if (!searchPanel.contains(document.activeElement) && document.activeElement !== searchInput) {
        hidePanel();
      }
    }, 150);
  });

  searchInput.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (state.q) {
        state.q = '';
        searchInput.value = '';
        if (searchClearBtn) searchClearBtn.hidden = true;
        if (panelOpen) {
          panelEmpty.hidden = false;
          panelResults.hidden = true;
        }
        refresh({ resetFold: true, pushHistory: false });
      } else {
        hidePanel();
        searchInput.blur();
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      clearTimeout(inputDebounce);
      state.q = searchInput.value;
      refresh({ resetFold: true, pushHistory: true });
      hidePanel();
    }
  });

  if (searchClearBtn) {
    searchClearBtn.addEventListener('click', function () {
      state.q = '';
      searchInput.value = '';
      searchClearBtn.hidden = true;
      if (panelOpen) {
        panelEmpty.hidden = false;
        panelResults.hidden = true;
      }
      searchInput.focus();
      refresh({ resetFold: true, pushHistory: true });
    });
  }

  suggestions.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var q = btn.getAttribute('data-lib-suggest') || '';
      searchInput.value = q;
      state.q = q;
      if (searchClearBtn) searchClearBtn.hidden = !q;
      showPanel();
      searchInput.focus();
      refresh({ resetFold: true, pushHistory: true });
    });
  });

  topicChips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      var h = chip.getAttribute('data-topic-handle');
      if (!h) return;
      state.topic = h;
      state.q = '';
      searchInput.value = '';
      if (searchClearBtn) searchClearBtn.hidden = true;
      hidePanel();
      refresh({ resetFold: true, pushHistory: true });
      var allGuides = document.getElementById('all-guides');
      if (allGuides) allGuides.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  searchPanel.addEventListener('click', function (e) {
    var topicBtn = e.target.closest('[data-result-topic]');
    if (topicBtn) {
      e.preventDefault();
      var h = topicBtn.getAttribute('data-result-topic');
      state.topic = h;
      state.q = '';
      searchInput.value = '';
      if (searchClearBtn) searchClearBtn.hidden = true;
      hidePanel();
      refresh({ resetFold: true, pushHistory: true });
      var allGuides = document.getElementById('all-guides');
      if (allGuides) allGuides.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  clearBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.topic = 'all';
      state.stage = 'all';
      state.q     = '';
      searchInput.value = '';
      if (searchClearBtn) searchClearBtn.hidden = true;
      refresh({ resetFold: true, pushHistory: true });
    });
  });

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', function () {
      visibleLimit += INITIAL_COUNT;
      applyFilters();
    });
  }

  document.addEventListener('click', function (e) {
    if (!panelOpen) return;
    if (searchArea.contains(e.target)) return;
    hidePanel();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== '/') return;
    if (document.activeElement === searchInput) return;
    var tag = (e.target && e.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
    e.preventDefault();
    searchInput.focus();
  });

  window.addEventListener('popstate', function () {
    hydrateFromURL();
    visibleLimit = INITIAL_COUNT;
    applyFilters();
  });

  /* Hide topic chips whose topic has 0 articles in this blog. */
  (function revealUsedTopicChips() {
    var used = {};
    searchIndex.forEach(function (e) { if (e.ph) used[e.ph] = true; });
    topicChips.forEach(function (chip) {
      chip.hidden = !used[chip.getAttribute('data-topic-handle')];
    });
  })();

  /* ─────────────────────────────────────────────
   * Init
   * ───────────────────────────────────────────── */
  hydrateFromURL();
  if (searchClearBtn) searchClearBtn.hidden = !state.q;
  applyFilters();
  syncURL(false); // strip legacy ?funnel= alias without polluting history
})();
